import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useRole } from '../context/RoleContext';
import type { GroupeMessagesAEnvoyer, ListeMessagesAEnvoyer } from '../types';
import { formatDateCourt } from '../components/demande/format';
import { EtatMessage } from '../components/messages/EtatMessage';
import { dePersonne, LIBELLE_CANAL } from '../components/messages/format';
import { MessagesModal } from '../components/messages/MessagesModal';
import { annoncerMiseAJourMessages } from '../components/messages/useCompteMessages';

// « Messages à envoyer » (gestion) : seulement ce qui demande une action — un message Slack qui n'est pas parti, un Slack que
// l'intervenant·e n'a pas publié depuis une demi-journée, des e-mails aux extérieur·e·s, des SMS d'annulation du jour.
// Maquette B-06, docs/DECISIONS.md §24. L'historique complet reste dans Administration.

const pluriel = (n: number, mot: string): string => `${n} ${mot}${n > 1 ? 's' : ''}`;

/** Durée écoulée : « 5 h », « hier », « 3 jours » (avec « depuis » ou « il y a » devant, selon la phrase). */
function ecoule(iso: string): string {
  const heures = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000));
  if (heures < 1) return "moins d'une heure";
  if (heures < 24) return `${heures} h`;
  const jours = Math.floor(heures / 24);
  return jours === 1 ? 'un jour' : `${jours} jours`;
}
const depuis = (iso: string): string => (ecoule(iso) === 'un jour' ? 'depuis hier' : `depuis ${ecoule(iso)}`);
const ilYA = (iso: string): string => (ecoule(iso) === "moins d'une heure" ? "il y a moins d'une heure" : `il y a ${ecoule(iso)}`);

function resume(g: GroupeMessagesAEnvoyer): string {
  if (g.canal === 'sms') {
    // « SMS » ne prend pas de s au pluriel
    const sms = `${g.nb_total} SMS`;
    return g.nb_sans_telephone > 0 ? `${sms} + ${pluriel(g.nb_sans_telephone, 'personne')} à appeler` : sms;
  }
  if (g.canal === 'email') {
    return `${pluriel(g.nb_a_envoyer + g.nb_pas_parti, 'e-mail')} à envoyer, un par remplaçant·e extérieur·e`;
  }
  const qui = g.lance_par_nom ?? "l'équipe gestion";
  if (g.statut_global === 'erreur') return `Message ${dePersonne(qui)} · l'envoi automatique a échoué ${ilYA(g.depuis)}`;
  return g.a_charge_de === 'intervenant'
    ? `${g.lance_par_nom ?? "L'intervenant·e"} n'a pas encore publié son message · ${depuis(g.depuis)}`
    : `À publier par ${qui} · ${depuis(g.depuis)}`;
}

function Ligne({ g, onOuvrir }: { g: GroupeMessagesAEnvoyer; onOuvrir: () => void }) {
  const titre = g.canal === 'sms'
    ? `${g.atelier_nom ?? 'Atelier'} · annulé aujourd'hui`
    : `${g.atelier_nom ?? 'Atelier'} · ${formatDateCourt(g.date_atelier)}`;
  return (
    <div className="msg" data-etat={g.statut_global}>
      <div className="msg-haut">
        <span className={`msg-canal${g.canal === 'slack' ? ' msg-canal--slack' : ''}`}>{LIBELLE_CANAL[g.canal]}</span>
        <b>{titre}</b>
        <EtatMessage statut={g.statut_global} />
      </div>
      <div className="msg-meta">{resume(g)}</div>
      <div className="msg-actions">
        <button type="button" className="btn btn-sm btn-accent" onClick={onOuvrir}
          aria-label={`Ouvrir les messages : ${titre}`}>Ouvrir les messages</button>
      </div>
    </div>
  );
}

export function MessagesAEnvoyerPage() {
  const { isAdmin } = useRole();
  const [liste, setListe] = useState<ListeMessagesAEnvoyer | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<GroupeMessagesAEnvoyer | null>(null);

  const charger = useCallback(async () => {
    try {
      setListe(await api.getMessagesAEnvoyer());
      setErreur(null);
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'La liste ne s\'est pas chargée.');
    }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  const groupes = liste?.groupes ?? [];
  const aujourdhui = groupes.filter((g) => g.groupe === 'aujourdhui');
  const recherches = groupes.filter((g) => g.groupe === 'recherches');
  const chezLesIntervenants = liste?.en_attente_intervenant ?? 0;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <span className="page-title">Messages à envoyer</span>
          <span className="page-meta">
            {"Ce que l'app a préparé et que personne n'a encore envoyé."}{isAdmin ? ' Le détail de tous les messages est dans Administration.' : ''}
          </span>
        </div>
      </div>

      {erreur && <div className="badge badge-err mb-12" style={{ display: 'block' }} role="alert">{erreur}</div>}
      {liste == null && !erreur && <div className="spinner" role="status" aria-label="Chargement de la liste" />}

      {liste != null && groupes.length === 0 && (
        <div className="bandeau-calme" role="status">✓ Rien à envoyer pour l'instant.</div>
      )}

      {aujourdhui.length > 0 && (
        <section className="msg-section" aria-labelledby="msg-aujourdhui">
          <h2 className="msg-groupe" id="msg-aujourdhui">Aujourd'hui <small>urgences · {pluriel(aujourdhui.length, 'envoi')}</small></h2>
          {aujourdhui.map((g) => <Ligne key={`${g.demande_id}:${g.canal}`} g={g} onOuvrir={() => setOuvert(g)} />)}
        </section>
      )}
      {recherches.length > 0 && (
        <section className="msg-section" aria-labelledby="msg-recherches">
          <h2 className="msg-groupe" id="msg-recherches">Recherches en cours <small>{pluriel(recherches.length, 'envoi')}</small></h2>
          {recherches.map((g) => <Ligne key={`${g.demande_id}:${g.canal}`} g={g} onOuvrir={() => setOuvert(g)} />)}
        </section>
      )}

      {chezLesIntervenants > 0 && (
        <p className="msg-meta" role="note">
          {chezLesIntervenants === 1
            ? "1 message Slack attend encore son auteur·rice : il apparaîtra ici après une demi-journée sans publication."
            : `${chezLesIntervenants} messages Slack attendent encore leur auteur·rice : ils apparaîtront ici après une demi-journée sans publication.`}
        </p>
      )}

      {ouvert && (
        <MessagesModal
          demandeId={ouvert.demande_id}
          titreSeance={`${ouvert.atelier_nom ?? 'Atelier'}${ouvert.canal === 'sms' ? '' : `, ${formatDateCourt(ouvert.date_atelier)}`}`}
          onClose={() => setOuvert(null)}
          onChange={() => { annoncerMiseAJourMessages(); void charger(); }}
        />
      )}
    </div>
  );
}
